// UNA CIFRA DE DINERO ESCRITA A MANO -> UN NÚMERO.
//
// Lissette, 2026-09-14, URGENTE: *"en el cotizador de Marketplace, cuando me
// traigo un cliente y coloca los decimales, por ejemplo con 80 mil le coloco
// 800000"*.
//
// El ingreso del cliente es texto libre: se teclea a mano, entra por
// importaciones y viene escrito de todas las formas ("$80,000.00", "80000.00",
// "38.500", "80000"). La conversión que había tiraba TODO lo que no fuera un
// dígito:
//
//     String(v).replace(/[^0-9]/g, '')
//
// …así que el punto decimal desaparecía y sus decimales se pegaban a la cifra:
// "80000.0" salía 800000 y "$80,000.00" salía 8.000.000. **Multiplicar el
// ingreso por diez cambia el subsidio del Mercado**, o sea que el cliente ve un
// precio que no es el suyo. Nada avisaba: es un número creíble.
//
// ── LA REGLA, Y POR QUÉ ES AMBIGUA ─────────────────────────────────────────
//
// No se puede mirar solo el separador: "38.500" son treinta y ocho mil quinientos
// en media Latinoamérica y España, y "80,000.00" son ochenta mil aquí. Lo que
// desambigua es CUÁNTOS DÍGITOS van detrás:
//
//   1. Si aparecen los DOS (punto y coma), manda el ÚLTIMO: es el decimal.
//      "80,000.00" -> 80000     ·  "80.000,50" -> 80000.5
//   2. Con uno solo, repetido, todos son de millar.  "1.234.567" -> 1234567
//   3. Con uno solo, una vez y seguido de EXACTAMENTE 3 dígitos, es de millar.
//      "38.500" -> 38500        ·  "80,000" -> 80000
//   4. En cualquier otro caso es decimal.  "80000.0" -> 80000  ·  "80,50" -> 80.5
//
// El caso que queda ambiguo de verdad —"1.500"— se resuelve como MILLAR, que es
// lo que ya hacía y lo que tiene sentido en un ingreso anual: nadie gana
// $1,50 al año.
//
// Vive en su propio archivo, sin dependencias, para que se pueda probar de
// verdad — es una cuenta que decide el PRECIO que ve el cliente.

/**
 * @param {unknown} v texto tal cual está en la ficha
 * @returns {number|null} el importe, o null si no hay ninguna cifra dentro
 */
export function importeDeTexto(v) {
  const bruto = String(v ?? '').trim();
  if (!bruto) return null;
  // Fuera el símbolo de moneda, los espacios y cualquier letra ("USD", "aprox").
  const limpio = bruto.replace(/[^0-9.,]/g, '');
  if (!/\d/.test(limpio)) return null;

  const puntos = (limpio.match(/\./g) || []).length;
  const comas = (limpio.match(/,/g) || []).length;

  let separadorDecimal = '';
  if (puntos && comas) {
    // Manda el último: el otro es de millar.
    separadorDecimal = limpio.lastIndexOf('.') > limpio.lastIndexOf(',') ? '.' : ',';
  } else if (puntos === 1 || comas === 1) {
    const sep = puntos ? '.' : ',';
    const detras = limpio.length - limpio.lastIndexOf(sep) - 1;
    // Tres dígitos detrás = separador de millar ("38.500"). Uno, dos o más de
    // tres = decimal ("80000.0", "1234.56").
    if (detras !== 3) separadorDecimal = sep;
  }
  // Con el mismo separador repetido (o ninguno) no hay decimales: todo millar.

  let normalizado = limpio;
  if (separadorDecimal) {
    const otro = separadorDecimal === '.' ? ',' : '.';
    normalizado = limpio.split(otro).join('');
    const i = normalizado.lastIndexOf(separadorDecimal);
    normalizado = `${normalizado.slice(0, i).replace(/[.,]/g, '')}.${normalizado.slice(i + 1)}`;
  } else {
    normalizado = limpio.replace(/[.,]/g, '');
  }

  const n = Number(normalizado);
  return Number.isFinite(n) ? n : null;
}

/**
 * El mismo importe redondeado a DÓLARES ENTEROS, que es lo que pide el
 * Mercado (y lo que el cotizador manda). `null` si no había cifra.
 */
export function dolaresDeTexto(v) {
  const n = importeDeTexto(v);
  return n === null ? null : Math.round(n);
}
